import React from 'react'
import PropTypes from 'prop-types'
import { getLocaleTimeString } from '../../helpers'
import { DAY_VIEW } from '../../helpers/constants'

import TimelineHeader from './Header'
import Style from './style'


const oneHourTimeStamp = 1000 * 60 * 60
const oneDayTimeStamp = oneHourTimeStamp * 24
const timezoneOffset = new Date().getTimezoneOffset() * 60 * 1000
const defaultFunction = () => { }
const COLUMN_GUTTER = 6
const COMPACT_EVENT_HEIGHT = 24
const TALL_EVENT_HEIGHT = 90
let isHoliday = false

class Timeline extends React.Component {
  constructor(props) {
    super(props)
    this.resetComponentState()
    this.ref = React.createRef()
    this.activeElement = React.createRef()
    this.scrollElement = React.createRef()
    this.nowLineRef = React.createRef()
    this.timelineRefs = []
    this.animationTimer = null
    this.nowLineTimer = null
  }
  resetComponentState () {
    this._state = {
      draggingNode: null,
      originalData: null,
      resizingData: null,
      mouseDownPosY: 0,
      currentPosY: 0,
      currentHeight: 0,
      scrollTopPos: 0,
      offsetClick: 0,
      leftoverClick: 0,
      isDrewDraggingNode: false,
      scrollHeight: 0,
      isDragging: false,
      isResizing: false,
      currentIndex: -1
    }
  }

  componentDidMount () {
    document.addEventListener('mouseup', (event) => this.handleDragEnd(event))
    document.addEventListener('mousemove', (event) => this.handleDrag(event))
    this.minuteStep = this.props.minuteStep ? this.props.minuteStep : 15
    this.hourHeight = 60
    this.stepHeight = (this.hourHeight / (60 / this.minuteStep))

    if (this.activeElement.current) {
      const scrollElement = this.scrollElement.current
      const boundingScroll = scrollElement.getBoundingClientRect()
      const boundingBox = this.activeElement.current.getBoundingClientRect()
      let scrollValue = 0
      if (boundingScroll.height > boundingBox.height) {
        scrollValue = scrollElement.scrollTop - ((boundingScroll.height - boundingBox.height) / 2) - boundingScroll.y + boundingBox.y
      } else {
        scrollValue = scrollElement.scrollTop - boundingScroll.y + boundingBox.y
      }
      this.scrollElement.current.scrollTop = scrollValue
    } else if (this.scrollElement.current && this.props.scrollToView) {
      const elementHeight = this.scrollElement.current.getBoundingClientRect().height
      const totalHeight = this.scrollElement.current.scrollHeight + elementHeight
      let percentScroll = ((new Date().valueOf() - timezoneOffset) % oneDayTimeStamp) / oneDayTimeStamp
      if (percentScroll < 0) percentScroll = 0
      else if (percentScroll > 1) percentScroll = 1
      const scrollValue = Math.floor((percentScroll * totalHeight) - (elementHeight / 2))
      this.scrollElement.current.scrollTop = scrollValue
    }

    this.nowLineTimer = setInterval(() => this.updateNowLine(), 60000)
  }

  componentWillUnmount () {
    if (this.nowLineTimer) clearInterval(this.nowLineTimer)
  }

  getNowLineTop () {
    const now = new Date()
    const pxPerMinute = (this.hourHeight || 60) / 60
    return ((now.getHours() * 60) + now.getMinutes()) * pxPerMinute
  }

  updateNowLine () {
    if (this.nowLineRef.current) {
      this.nowLineRef.current.style.top = `${this.getNowLineTop()}px`
    }
  }

  componentDidUpdate () {
    this.minuteStep = this.props.minuteStep ? this.props.minuteStep : 15
    this.hourHeight = 60
    this.stepHeight = (this.hourHeight / (60 / this.minuteStep))
    if (this.activeElement.current) {
      const scrollElement = this.scrollElement.current
      const boundingScroll = scrollElement.getBoundingClientRect()
      const boundingBox = this.activeElement.current.getBoundingClientRect()
      let scrollValue = 0
      if (boundingScroll.height > boundingBox.height) {
        scrollValue = scrollElement.scrollTop - ((boundingScroll.height - boundingBox.height) / 2) - boundingScroll.y + boundingBox.y
      } else {
        scrollValue = scrollElement.scrollTop - boundingScroll.y + boundingBox.y
      }
      this.scrollElement.current.scrollTop = scrollValue
    } else if (this.scrollElement.current && this.props.scrollToView) {
      const elementHeight = this.scrollElement.current.getBoundingClientRect().height
      const totalHeight = this.scrollElement.current.scrollHeight + elementHeight
      let percentScroll = ((new Date().valueOf() - timezoneOffset) % oneDayTimeStamp) / oneDayTimeStamp
      if (percentScroll < 0) percentScroll = 0
      else if (percentScroll > 1) percentScroll = 1
      const scrollValue = Math.floor((percentScroll * totalHeight) - (elementHeight / 2))
      this.scrollElement.current.scrollTop = scrollValue
    }
  }

  componentWillUnmount () {
    document.removeEventListener('mouseup', (event) => this.handleDragEnd(event))
    document.removeEventListener('mousemove', (event) => this.handleDrag(event))
  }

  animateProp (element, propertyName, newValue, duration, framerate, delay = 0) {
    if (element == null) return false
    const oldValue = element[propertyName]
    const stepTime = (1 / framerate) * 1000
    const maxStepCount = duration / stepTime
    const stepValue = (newValue - oldValue) / maxStepCount
    const countTimer = 0
    if (this.animationTimer != null) clearTimeout(this.scrollTimer1)
    this.animationTimer = setTimeout(() => this.doAnimation(element, propertyName, oldValue + stepValue, newValue, countTimer, maxStepCount, stepValue, stepTime), stepTime + delay)
  }

  doAnimation (element, propertyName, value, maxValue, count, maxCount, stepValue, stepTime) {
    element[propertyName] = value
    count = count + 1
    if (count > maxCount) {
      this.scrollTimer1 = null
      return null
    } else if (count >= maxCount) {
      this.animationTimer = setTimeout(() => this.doAnimation(element, propertyName, maxValue, maxValue, count, maxCount, stepValue, stepTime), stepTime)
    } else {
      this.animationTimer = setTimeout(() => this.doAnimation(element, propertyName, value + stepValue, maxValue, count, maxCount, stepValue, stepTime), stepTime)
    }
  }

  handleResizeStart (event, index, idx) {
    if (event.button === 0) {
      if (this._state.draggingNode === null) {
        const node = event.currentTarget.parentElement
        const scrollTop = node.parentElement.parentElement.scrollTop
        const currentTimestamp = node.parentElement.getAttribute('timestamp')
        const bookingList = this.bookingList[currentTimestamp].slice()
        this._state = {
          draggingData: { ...bookingList[idx] },
          originalData: { ...bookingList[idx] },
          draggingNode: null,
          draggedNode: node,
          scrollTopPos: scrollTop,
          mouseDownPosY: event.clientY + scrollTop,
          currentPosY: event.clientY + scrollTop,
          currentHeight: node.getBoundingClientRect().height,
          scrollHeight: node.parentElement.getBoundingClientRect().height,
          isResizing: true,
          currentIndex: index
        }
        event.stopPropagation()
        event.preventDefault()
      }
    }
  }

  handleDragStart (event, index, idx) {
    if (event.button === 0) {
      if (this._state.draggingNode === null) {
        const node = event.currentTarget.parentElement
        const scrollTop = node.parentElement.parentElement.scrollTop
        const currentTimestamp = node.parentElement.getAttribute('timestamp')
        const bookingList = this.bookingList[currentTimestamp].slice()
        const boundingBox = node.getBoundingClientRect()
        this._state = {
          draggingData: { ...bookingList[idx] },
          originalData: { ...bookingList[idx] },
          draggingNode: null,
          draggedNode: node,
          scrollTopPos: scrollTop,
          mouseDownPosY: event.clientY + scrollTop,
          currentPosY: event.clientY + scrollTop,
          offsetClick: event.clientY - boundingBox.y,
          leftoverClick: boundingBox.bottom - event.clientY,
          scrollHeight: node.parentElement.getBoundingClientRect().height,
          isDragging: true,
          currentIndex: index
        }
        event.stopPropagation()
        event.preventDefault()
      }
    }
  }

  handleDragEnd (event) {
    if (event.button === 0) {
      const { draggedNode, draggingNode, draggingData, originalData } = this._state
      if (draggingNode !== null) {
        draggingNode.remove()
        draggedNode.classList.toggle('dragged', false)
        this.ref.current.classList.toggle('dragging', false)
        this.ref.current.classList.toggle('resizing', false)
        document.body.style.cursor = 'auto'
        if (this.props.handleEdit && originalData.endDatetime.valueOf() !== draggingData.endDatetime.valueOf()) {
          this.props.handleEdit(draggingData)
        }
        setTimeout(() => this.resetComponentState(), 0)
        event.stopPropagation()
        event.preventDefault()
      }
    }
  }

  handleDrag (event) {
    const { draggingNode, isDragging, isResizing } = this._state
    const { startOperatingTime, endOperatingTime } = this.props
    if (isDragging) {
      const { draggedNode, draggingData, currentPosY, offsetClick, leftoverClick, scrollHeight, mouseDownPosY, currentIndex } = this._state
      const node = this.timelineRefs[currentIndex].current
      const currentTimestamp = parseInt(node.getAttribute('timestamp'))
      const currentDate = new Date(currentTimestamp)
      const scrollTopPos = node.parentElement.scrollTop
      const elementOffsetY = node.parentElement.getBoundingClientRect().y
      const elementOffsetBtm = elementOffsetY + scrollHeight - this.stepHeight
      const diffPosY = event.clientY + scrollTopPos - currentPosY
      let posY = currentPosY + diffPosY
      const offsetSpace = (posY - offsetClick) - elementOffsetY
      const offsetSpaceBtm = elementOffsetBtm - (posY + leftoverClick)
      if (offsetSpace < 0) {
        posY = elementOffsetY + offsetClick
        if (diffPosY < 0 && offsetClick + diffPosY >= this.stepHeight) {
          this._state.offsetClick = offsetClick + diffPosY
          this._state.leftoverClick = leftoverClick - diffPosY
          this._state.mouseDownPosY = mouseDownPosY + diffPosY
        }
      } else if (offsetSpaceBtm < 0) {
        posY = elementOffsetBtm - leftoverClick
        if (diffPosY > 0 && leftoverClick - diffPosY >= this.stepHeight) {
          this._state.offsetClick = offsetClick + diffPosY
          this._state.leftoverClick = leftoverClick - diffPosY
          this._state.mouseDownPosY = mouseDownPosY + diffPosY
        }
      }
      if (posY !== currentPosY) {
        let draggingNod = draggingNode
        if (!draggingNod) {
          draggingNod = draggedNode.cloneNode(true)
          this._state.draggingNode = draggingNod
          draggedNode.classList.toggle('dragged', true)
          draggingNod.classList.toggle('dragging', true)
          this.ref.current.classList.toggle('dragging', true)
          draggingNod.classList.toggle('animate-fade-down', true)
          document.body.style.cursor = 'move'
          node.appendChild(draggingNod)
        }
        const startDatetime = this.getDatetimeFromLenght(posY - offsetClick - elementOffsetY, currentTimestamp)
        const endDatetime = this.getDatetimeFromLenght(posY + leftoverClick - elementOffsetY, currentTimestamp)
        if (startOperatingTime && endOperatingTime) {
          const startOperatingDatetime = new Date(`${currentDate.getFullYear()}/${(currentDate.getMonth() + 1).toString().padStart('0', 2)}/${currentDate.getDate().toString().padStart('0', 2)} `.concat(startOperatingTime))
          const endOperatingDatetime = new Date(`${currentDate.getFullYear()}/${(currentDate.getMonth() + 1).toString().padStart('0', 2)}/${currentDate.getDate().toString().padStart('0', 2)} `.concat(endOperatingTime))
          if (
            startDatetime.valueOf() + timezoneOffset < startOperatingDatetime.valueOf() ||
            endDatetime.valueOf() + timezoneOffset > endOperatingDatetime.valueOf()
          ) {
            return null
          }
        }
        const translateY = this.snapPosToMinuteStep(posY - mouseDownPosY)
        draggingNod.style.transform = `translateY(${translateY}px)`
        draggingData.startDatetime = startDatetime
        draggingData.endDatetime = endDatetime
        draggingNod.childNodes[0].childNodes[1].childNodes[0].innerHTML = getLocaleTimeString(new Date(startDatetime.valueOf() + timezoneOffset))
        draggingNod.childNodes[0].childNodes[1].childNodes[2].innerHTML = getLocaleTimeString(new Date(endDatetime.valueOf() + timezoneOffset))
      }
      this._state.currentPosY = posY
    } else if (isResizing) {
      const { draggedNode, draggingData, currentPosY, currentHeight, scrollTopPos, scrollHeight, mouseDownPosY, currentIndex } = this._state
      const node = this.timelineRefs[currentIndex].current
      const currentTimestamp = parseInt(node.getAttribute('timestamp'))
      const currentDate = new Date(currentTimestamp)
      const elementOffsetY = node.parentElement.getBoundingClientRect().y
      const elementOffsetBtm = elementOffsetY + scrollHeight - this.stepHeight
      let posY = event.clientY + scrollTopPos
      const offsetSpace = posY - mouseDownPosY + currentHeight - this.stepHeight
      const offsetSpaceBtm = posY - elementOffsetBtm
      if (offsetSpace < 0) {
        posY = currentPosY
      } else if (offsetSpaceBtm > 0) {
        posY = elementOffsetBtm
      }
      if (posY !== currentPosY) {
        let draggingNod = draggingNode
        if (!draggingNod) {
          draggingNod = draggedNode.cloneNode(true)
          this._state.draggingNode = draggingNod
          draggedNode.classList.toggle('dragged', true)
          draggingNod.classList.toggle('resizing', true)
          this.ref.current.classList.toggle('resizing', true)
          draggingNod.classList.toggle('animate-fade-down', true)
          document.body.style.cursor = 'ns-resize'
          node.appendChild(draggingNod)
        }
        const height = this.snapPosToMinuteStep(posY - mouseDownPosY)
        const endDatetime = this.getDatetimeFromLenght(height + mouseDownPosY - elementOffsetY, currentTimestamp)
        if (endOperatingTime) {
          const endOperatingDatetime = new Date(`${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart('0', 2)}-${currentDate.getDate().toString().padStart('0', 2)} `.concat(endOperatingTime))
          if (
            endDatetime.valueOf() + timezoneOffset > endOperatingDatetime.valueOf()
          ) {
            return null
          }
        }
        draggingNod.style.height = `${height + currentHeight}px`
        draggingData.endDatetime = endDatetime
        draggingNod.childNodes[0].childNodes[1].childNodes[2].innerHTML = getLocaleTimeString(new Date(draggingData.endDatetime.valueOf() + timezoneOffset))
        this._state.currentPosY = posY
      }
    }
  }

  handleClickEvent (event, index, idx) {
    event.stopPropagation()
    event.preventDefault()
    const node = event.currentTarget.parentElement
    const currentTimestamp = node.parentElement.getAttribute('timestamp')
    const bookingList = this.bookingList[currentTimestamp].slice()
    if (this.props.handleClickEvent) {
      this.props.handleClickEvent(event, bookingList[idx])
    }
    this.resetComponentState()
  }

  handleClickTimeline (event) {
    const { isResizing, isDragging } = this._state
    if (!isResizing && !isDragging) {
      const { handleClickTimeline, startOperatingTime, endOperatingTime, minuteStep } = this.props
      const node = event.currentTarget
      let currentTimestamp = parseInt(node.getAttribute('timestamp'))
      const selectedDate = new Date(currentTimestamp)
      currentTimestamp = this.getDatetimeFromLenght(
        event.clientY + node.parentElement.scrollTop - node.parentElement.getBoundingClientRect().y,
        currentTimestamp
      )
      const currentDate = new Date(currentTimestamp)
      const realClickTime = currentDate.valueOf() + timezoneOffset
      if (realClickTime < new Date().setHours(new Date().getHours(), 0, 0, 0)) {
        return null
      }

      if (startOperatingTime && endOperatingTime) {
        const startOperatingDatetime = new Date(`${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart('0', 2)}-${selectedDate.getDate().toString().padStart('0', 2)} `.concat(startOperatingTime))
        const endOperatingDatetime = new Date(`${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart('0', 2)}-${selectedDate.getDate().toString().padStart('0', 2)} `.concat(endOperatingTime))
        if (
          realClickTime < startOperatingDatetime.valueOf() ||
          realClickTime > endOperatingDatetime.valueOf() - (minuteStep * 60000)
        ) {
          return null
        }
      }
      if (handleClickTimeline) {
        handleClickTimeline(currentTimestamp.valueOf())
      }
    }
  }

  handleScroll (event) {
    const { draggingNode, isDragging, isResizing } = this._state
    if (draggingNode !== null && isDragging) {
      const { draggingData, draggedNode, currentPosY, scrollTopPos, offsetClick, leftoverClick, scrollHeight, mouseDownPosY, currentIndex } = this._state
      const node = this.timelineRefs[currentIndex].current
      const currentTimestamp = parseInt(node.getAttribute('timestamp'))
      const elementOffsetY = node.parentElement.getBoundingClientRect().y
      const elementOffsetBtm = elementOffsetY + scrollHeight - this.stepHeight
      const newScrollTop = node.parentElement.scrollTop
      const diffScroll = newScrollTop - scrollTopPos
      let posY = currentPosY + diffScroll
      const offsetSpace = (posY - offsetClick) - elementOffsetY
      const offsetSpaceBtm = elementOffsetBtm - (posY + leftoverClick)
      if (offsetSpace < 0) {
        posY = elementOffsetY + offsetClick
        if (diffScroll < 0 && offsetClick + diffScroll >= this.stepHeight) {
          this._state.offsetClick = offsetClick + diffScroll
          this._state.leftoverClick = leftoverClick - diffScroll
          this._state.mouseDownPosY = mouseDownPosY + diffScroll
        }
      } else if (offsetSpaceBtm < 0) {
        posY = elementOffsetBtm - leftoverClick
        if (diffScroll > 0 && offsetClick + diffScroll >= this.stepHeight) {
          this._state.offsetClick = offsetClick + diffScroll
          this._state.leftoverClick = leftoverClick - diffScroll
          this._state.mouseDownPosY = mouseDownPosY + diffScroll
        }
      }
      if (posY !== currentPosY) {
        let draggingNod = draggingNode
        if (!draggingNod) {
          draggingNod = draggedNode.cloneNode(true)
          this._state.draggingNode = draggingNod
          draggedNode.classList.toggle('dragged', true)
          draggingNod.classList.toggle('dragging', true)
          this.ref.current.classList.toggle('dragging', true)
          draggingNod.classList.toggle('animate-fade-down', true)
          document.body.style.cursor = 'move'
          node.appendChild(draggingNod)
        }
        const translateY = this.snapPosToMinuteStep(posY - mouseDownPosY)
        draggingNod.style.transform = `translateY(${translateY}px)`

        draggingData.startDatetime = this.getDatetimeFromLenght(posY - offsetClick - elementOffsetY, currentTimestamp)
        draggingData.endDatetime = this.getDatetimeFromLenght(posY + leftoverClick - elementOffsetY, currentTimestamp)
        draggingNod.childNodes[0].childNodes[1].childNodes[0].innerHTML = getLocaleTimeString(new Date(draggingData.startDatetime.valueOf() + timezoneOffset))
        draggingNod.childNodes[0].childNodes[1].childNodes[2].innerHTML = getLocaleTimeString(new Date(draggingData.endDatetime.valueOf() + timezoneOffset))

        this._state.scrollTopPos = newScrollTop
      }
      this._state.currentPosY = posY
    } else if (draggingNode !== null && isResizing) {
      const { draggingData, draggedNode, currentPosY, currentHeight, scrollTopPos, scrollHeight, mouseDownPosY, currentIndex } = this._state
      const node = this.timelineRefs[currentIndex].current
      const currentTimestamp = parseInt(node.getAttribute('timestamp'))
      const elementOffsetY = node.parentElement.getBoundingClientRect().y
      const elementOffsetBtm = elementOffsetY + scrollHeight - this.stepHeight
      const newScrollTop = node.parentElement.scrollTop
      const diffScroll = newScrollTop - scrollTopPos
      let posY = currentPosY + diffScroll
      const offsetSpace = posY - mouseDownPosY + currentHeight - this.stepHeight
      const offsetSpaceBtm = posY - elementOffsetBtm
      if (offsetSpace < 0) {
        posY = currentPosY
      } else if (offsetSpaceBtm > 0) {
        posY = elementOffsetBtm
      }
      if (posY !== currentPosY) {
        const draggingNod = draggingNode
        if (!draggingNod) {
          const draggingNod = draggedNode.cloneNode(true)
          this._state.draggingNode = draggingNod
          draggedNode.classList.toggle('dragged', true)
          draggingNod.classList.toggle('resizing', true)
          this.ref.current.classList.toggle('resizing', true)
          draggingNod.classList.toggle('animate-fade-down', true)
          document.body.style.cursor = 'ns-resize'
          node.appendChild(draggingNod)
        }
        const height = this.snapPosToMinuteStep(posY - mouseDownPosY)
        draggingNod.style.height = `${height + currentHeight}px`
        draggingData.endDatetime = this.getDatetimeFromLenght(height + mouseDownPosY - elementOffsetY, currentTimestamp)
        draggingNod.childNodes[0].childNodes[1].childNodes[2].innerHTML = getLocaleTimeString(new Date(draggingData.endDatetime.valueOf() + timezoneOffset))
        event.stopPropagation()
        event.preventDefault()
        this._state.currentPosY = posY
        this._state.scrollTopPos = newScrollTop
      }
    }
  }

  handleMouseOut (event, index, timeStamp) {
    console.log('handleMouseOut', index, new Date(timeStamp))
  }

  handleMouseEnter (event, index, timeStamp) {
    const { draggingNode, draggedNode, draggingData, isDragging, currentIndex } = this._state
    if (draggingNode !== null && isDragging && currentIndex !== index) {
      event.currentTarget.appendChild(draggingNode)
      draggingData.startDatetime = new Date(this.changeDataTimestamp(draggingData.startDatetime, timeStamp))
      draggingData.endDatetime = new Date(this.changeDataTimestamp(draggingData.endDatetime, timeStamp))
      this._state.currentIndex = index
      draggedNode.classList.toggle('dragged', true)
      draggingNode.classList.toggle('dragging', true)
      draggingNode.classList.toggle('animate-fade-down', true)
      document.body.style.cursor = 'moving'
      this._state.isDrewDraggingNode = true
    }
  }

  handleClickHeader (currentTimestamp) {
    if (this.props.handleClickHeader) {
      this.props.handleClickHeader(currentTimestamp)
    }
  }

  snapPosToMinuteStep (posY) {
    return Math.floor(Math.round(posY / this.stepHeight) * this.stepHeight)
  }

  changeDataTimestamp (oldTimestamp, newTimestamp) {
    const leftOver = oldTimestamp % oneDayTimeStamp
    return newTimestamp + leftOver
  }

  getStartDateTimestamp (currentTimestamp) {
    return Math.floor(currentTimestamp / oneDayTimeStamp) * oneDayTimeStamp
  }

  getDatetimeFromLenght (posY, currentTimestamp) {
    const startOfDayTimestamp = this.getStartDateTimestamp(currentTimestamp)
    const totalMinute = Math.floor(Math.round(posY / this.stepHeight) * this.minuteStep)
    return new Date(startOfDayTimestamp + (totalMinute * 60 * 1000))
  }

  groupEvent () {
    const result = {}
    const username = this.props.username
    for (const booking of this.props.bookingList) {
      booking.startDatetime = new Date(booking.startDatetime)
      booking.endDatetime = new Date(booking.endDatetime)
      if (booking.owner && username) {
        booking.isResizeable = username === booking.owner
        booking.isMoveable = booking.isResizeable
      }
      const startDay = this.getStartDateTimestamp(booking.startDatetime.valueOf())
      if (result[startDay]) result[startDay].push(booking)
      else result[startDay] = [booking]
    }
    this.bookingList = result
  }

  assignEventColumns (bookingList) {
    bookingList.forEach((booking) => {
      booking._lane = 0
      booking._columns = 1
    })

    const sorted = bookingList
      .filter((booking) => !booking.isActive)
      .sort((a, b) => (a.startDatetime - b.startDatetime) || (a.endDatetime - b.endDatetime))

    let cluster = []
    let clusterEnd = -Infinity
    const flushCluster = () => {
      if (!cluster.length) return
      const laneEnds = []
      cluster.forEach((booking) => {
        let lane = laneEnds.findIndex((end) => end <= booking.startDatetime.valueOf())
        if (lane === -1) lane = laneEnds.length
        laneEnds[lane] = booking.endDatetime.valueOf()
        booking._lane = lane
      })
      cluster.forEach((booking) => { booking._columns = laneEnds.length })
      cluster = []
      clusterEnd = -Infinity
    }

    sorted.forEach((booking) => {
      if (cluster.length && booking.startDatetime.valueOf() >= clusterEnd) flushCluster()
      cluster.push(booking)
      clusterEnd = Math.max(clusterEnd, booking.endDatetime.valueOf())
    })
    flushCluster()
  }

  renderEvent (currentTimestamp, index) {
    const bookingList = this.bookingList[currentTimestamp]
    if (!bookingList) return

    this.assignEventColumns(bookingList)

    return bookingList.map((booking, idx) => {
      const { isMoveable, isResizeable } = this.props
      const height = this.snapPosToMinuteStep(this.hourHeight * (booking.endDatetime - booking.startDatetime) / oneHourTimeStamp)
      const posTop = this.snapPosToMinuteStep(this.hourHeight * (booking.startDatetime - currentTimestamp) / oneHourTimeStamp)
      let eventSizeClass = ''
      if (height < COMPACT_EVENT_HEIGHT) eventSizeClass = ' compact'
      else if (height > TALL_EVENT_HEIGHT) eventSizeClass = ' tall'

      let className = ''
      let ref
      let posLeft = '0px'
      let posRight = '10px'
      if (booking.isActive) {
        className = ' active'
        ref = this.activeElement
      } else if (booking._columns > 1) {
        const columnWidth = 100 / booking._columns
        posLeft = `${booking._lane * columnWidth}%`
        posRight = `calc(${(booking._columns - 1 - booking._lane) * columnWidth}% + ${COLUMN_GUTTER}px)`
      }
      return (
        <section
          className={'event-container'.concat(className)}
          ref={ref}
          key={booking.id}
          style={{ top: `${posTop}px`, left: posLeft, right: posRight, height: `${height}px` }}
        >
          <article
            className={'event'.concat(eventSizeClass)}
            onClick={(event) => this.handleClickEvent(event, index, idx)}
            onMouseDown={booking.status === 2 && (booking.isActive || (isMoveable && booking.isMoveable)) ? (event) => this.handleDragStart(event, index, idx) : defaultFunction}
          >
            <h4>{booking.projectName}</h4>
            <p>
              <span>
                {getLocaleTimeString(new Date(booking.startDatetime.valueOf() + timezoneOffset))}
              </span>
              {' - '}
              <span>
                {getLocaleTimeString(new Date(booking.endDatetime.valueOf() + timezoneOffset))}
              </span>
            </p>
          </article>
          {booking.status === 2 && (booking.isActive || (isResizeable && booking.isResizeable))
            ? <section
              className={'resize-tool'.concat(booking.isResizeable ? ' cursor-pointer' : '')}
              onClick={(event) => this.handleClick(event)}
              onMouseDown={(event) => this.handleResizeStart(event, index, idx)}
            />
            : null}
        </section>
      )
    })
  }

  renderGridLine (currentTimestamp) {
    const gridLine = []
    let {
      startOperatingTime = '00:00:00',
      endOperatingTime = '23:59:59'
    } = this.props

    startOperatingTime = Number(startOperatingTime.split(':')[0])
    endOperatingTime = Number(endOperatingTime.split(':')[0])

    const dateNow = new Date()
    const hourNow = dateNow.getHours()

    const isToday = new Date(currentTimestamp).toDateString() === dateNow.toDateString()
    const isPastDate = new Date(currentTimestamp).setHours(0,0,0,0) < new Date().setHours(0,0,0,0)

    for (let i = 0; i < 24; i++) {
      const isTimeBelowNow = isPastDate || (isToday && i < hourNow)
      const isDisabledTime = startOperatingTime > i || i > endOperatingTime

      gridLine.push(
        <div
          className={`grid-line${isTimeBelowNow || isDisabledTime ? ' grid-line-border-closed' : ''}`}
          timestamp={currentTimestamp + (i * oneHourTimeStamp)}
          key={currentTimestamp + (i * oneHourTimeStamp)}
        />
      )
    }
    return gridLine
  }

  renderRowHeader () {
    let {
      startOperatingTime = '00:00:00',
      endOperatingTime = '23:59:59',
      selectedDate
    } = this.props

    const handleIsCloseLine = (isClose = false) => {
      return isClose ? 'grid-line-closed' : ''
    }

    startOperatingTime = Number(startOperatingTime.split(':')[0])
    endOperatingTime = Number(endOperatingTime.split(':')[0])

    const isDisabledLine = startOperatingTime > 0 || endOperatingTime <= 0
    const gridLine = [(
      <div
        className={`grid-line ${handleIsCloseLine(isDisabledLine)}`}
        key={0}
      />)
    ]

    const currentTime = new Date()
    const currentHours = currentTime.getHours()
    const currentDate = currentTime.getDate()
    const currentMonth = currentTime.getMonth()
    const currentYear = currentTime.getFullYear()

    const _selectedDate = selectedDate && selectedDate.getDate()
    const selectedMonth = selectedDate && selectedDate.getMonth()
    const selectedYear = selectedDate && selectedDate.getFullYear()

    const isDateDisable = (
      selectedYear < currentYear ||
      (selectedYear === currentYear && 
        (selectedMonth < currentMonth || 
          (selectedMonth === currentMonth && _selectedDate < currentDate)
        )
      )
    )
    
    for (let i = 1; i < 24; i++) {
      const isDisabledTime = startOperatingTime > i || i > endOperatingTime || isHoliday
      const isHourDisabled = (selectedYear === currentYear && selectedMonth === currentMonth && _selectedDate === currentDate && i < currentHours)
      const isBookingByDayDisabled = isDateDisable || isHourDisabled || isDisabledTime

      const hourTitleHeader = `${i.toString().padStart(2, '0')}:00`
      gridLine.push(
        <div
          className={`grid-line ${handleIsCloseLine(isBookingByDayDisabled)}`}
          key={i * oneHourTimeStamp}
        >
          {hourTitleHeader}
        </div>
      )
    }

    return (
      <section className='row-header'>
        {gridLine}
      </section>
    )
  }

  renderDisableMarkUp(currentTimestamp, isHoliday) {
    const markUp = []
    currentTimestamp = new Date(currentTimestamp)
    //Holiday text
    let name = ''
    if (isHoliday) {
      const dateString = [currentTimestamp.getFullYear(), (currentTimestamp.getMonth() + 1).toString().padStart(2, 0), currentTimestamp.getDate().toString().padStart(2, 0)].join('-')
      let holidayText = this.props.holidayListName.filter((holiday) => new Date(holiday.date).valueOf() === new Date(dateString).valueOf())
      if (holidayText.length > 0) {
        name = holidayText[0].holidayName
      }
      markUp.push(
        <div className='holiday-name'>{name}</div>
      )
    }
    return markUp
  }

  renderNowLine (currentTimestamp) {
    const isToday = new Date(currentTimestamp).toDateString() === new Date().toDateString()
    if (!isToday) return null

    return (
      <div className='now-line' ref={this.nowLineRef} style={{ top: `${this.getNowLineTop()}px` }}>
        <div className='now-line-dot' />
      </div>
    )
  }

  renderTimeline () {
    const {
      startDatetimeContent,
      endDatetimeContent,
    } = this.props
    this.startDay = this.getStartDateTimestamp(startDatetimeContent.valueOf())
    this.totalDay = Math.ceil((endDatetimeContent.valueOf() - this.startDay) / oneDayTimeStamp)
    const timeline = []
    for (let i = 0; i <= this.totalDay; i++) {
      const currentTimestamp = this.startDay + (i * oneDayTimeStamp)
      let ref

      if (this.timelineRefs.length > i) {
        ref = this.timelineRefs[i]
      } else {
        ref = React.createRef()
        this.timelineRefs.push(ref)
      }
      timeline.push(
        <section
          key={currentTimestamp}
          timestamp={currentTimestamp}
          ref={ref}
          className= 'timeline-container'
          onClick={(event) => this.handleClickTimeline(event)}
          onMouseEnter={(event) => this.handleMouseEnter(event, i, currentTimestamp)}
        >
          {this.renderGridLine(currentTimestamp)}
          {this.renderEvent(currentTimestamp, i)}
          {this.renderNowLine(currentTimestamp)}
        </section>
      )
    }
    return timeline
  }

  render () {
    const {
      colorTheme,
      startDatetimeHeader,
      endDatetimeHeader,
      selectedDate =  Date.now(),
      language,
      history,
      headerType,
      headerTextMonth,
      holidayList,
      startDatetimeContent
    } = this.props

    let isHoliday
    const selectedDateTimestamp = startDatetimeContent.getTime()
    if (holidayList && holidayList.length && holidayList.includes(selectedDateTimestamp)) {
      isHoliday = true
    } else {
      isHoliday = false
    }

    this.groupEvent()
    return (
      <Style
        colorTheme={colorTheme}
        ref={this.ref}
      >
        {headerType !== DAY_VIEW.HEADER_TYPE.CLOSE && (
          <TimelineHeader
            startDatetimeHeader={startDatetimeHeader}
            endDatetimeHeader={endDatetimeHeader}
            selectedDate={selectedDate}
            language={language}
            history={history}
            holidayList={holidayList}
            headerType={headerType}
            headerTextMonth={headerTextMonth}
          />
        )}
        {this.renderDisableMarkUp(selectedDate, isHoliday)}
        <section
          className='timeline-content'
          ref={this.scrollElement}
          onScroll={(event) => this.handleScroll(event)}
        >
          {this.renderRowHeader()}
          {this.renderTimeline()}
        </section>
      </Style>
    )
  }
}

Timeline.defaultProps = {
  colorTheme: '',
  headerType: DAY_VIEW.HEADER_TYPE.FULL,
  headerTextMonth: '',
}

Timeline.propTypes = {
  colorTheme: PropTypes.string,
  handleClickTimeline: PropTypes.func,
  handleEdit: PropTypes.func,
  isMoveable: PropTypes.bool,
  isResizeable: PropTypes.bool,
  holidayList: PropTypes.array,
  bookingList: PropTypes.array,
  scrollToView: PropTypes.bool,
  selectedDate: PropTypes.instanceOf(Date),
  startDatetimeHeader: PropTypes.instanceOf(Date),
  startDatetimeContent: PropTypes.instanceOf(Date).isRequired,
  endDatetimeHeader: PropTypes.instanceOf(Date),
  endDatetimeContent: PropTypes.instanceOf(Date).isRequired,
  minuteStep: PropTypes.number,
  startOperatingTime: PropTypes.string,
  endOperatingTime: PropTypes.string,
  headerType: PropTypes.oneOf([DAY_VIEW.HEADER_TYPE.MINI, DAY_VIEW.HEADER_TYPE.FULL, DAY_VIEW.HEADER_TYPE.CLOSE]),
  headerTextMonth: PropTypes.string,
}

export default Timeline
